import { Button, Drawer } from 'antd';
import { H5 } from 'components/ui/base/typography';
import { useTemplateContext } from 'components/ui/composed/template/TemplateContext';
import { setNestedObjectValues, useFormik } from 'formik';
import { find, isEmpty } from 'lodash';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { getFormikProps } from 'views/declarations/utils/form-utils';
import getAllDeclarationTemplateNames from 'utils/requests/getAllDeclarationTemplateNames';
import { useTranslation } from 'react-i18next';
import BulkUpload from 'components/new-ui/composed/bulkUpload/BulkUpload';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import {
    downloadProductTemplate,
    downloadUkProductTemplate,
    uploadProductTemplate,
    uploadUkProductTemplate,
    createUkBulkProducts,
    createBulkProducts,
} from 'store/products-templates/client';
import useRequest from 'hooks/useRequest';
import Notification from 'components/ui/base/notification/Notification';
import { Product } from './ProductTemplatesTable';
import CustomModal from 'components/ui/base/modal/Modal';
import ProductBulkUploadModal from 'views/custom-declaration/products/bulk-upload/ProductBulkUploadModal';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { MessageTypes } from 'store/declarations/enums/common/declaration-types';
import { useNavigate } from 'react-router-dom';
import { FileTypes } from 'utils/file-types';
import { getCreateTemplateValidationSchema } from '../validations/createTemplateInitialValidation';
import TeamSelectField from './TeamSelectField';
import { StyledCloseOutlined } from 'views/declarations/declaration-tabs/documentation/CustomerDocuments.styles';
import { TemplatesTab } from '../types';
import { getCreateTemplateInitialValues, getTemplateFormValues } from './createTemplateInitialValues';
import LabeledField from './LabeledField';
import FormSelect from 'components/new-ui/composed/formSelect/FormSelect';
import FormInput from 'components/new-ui/composed/formInput/FormInput';

interface FormData {
    country?: string;
    declarationType?: string;
    formType?: string;
    name?: string;
    declarationTemplateId?: string;
    teamId?: string;
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 2rem;
`;

interface Props {
    isFreightForwarder?: boolean;
    visible?: boolean;
    onClose: () => void;
    mode: TemplatesTab;
    country: DeclarationCountry;
    internalType: DeclarationInternalType;
    messageType: MessageTypes;
    onProductTemplatesCreated?: () => void;
    templateOptions?: {
        id: string;
        value?: string;
    }[];
}

const CreateTemplateDrawer = ({
    isFreightForwarder = false,
    visible,
    onClose,
    mode,
    templateOptions,
    country,
    internalType,
    messageType,
    onProductTemplatesCreated,
}: Props): ReactElement => {
    const { openModal } = useTemplateContext();
    const { t } = useTranslation('customs_declarations');
    const navigate = useNavigate();

    const [uploaded, setUploaded] = useState(false);
    const downloadRequest = country === DeclarationCountry.UK ? downloadUkProductTemplate : downloadProductTemplate;
    const uploadRequest = country === DeclarationCountry.UK ? uploadUkProductTemplate : uploadProductTemplate;
    const { doRequest: uploadProduct, error: uploadError, data: uploadedProducts } = useRequest(uploadRequest);
    const { doRequest: downloadTemplate } = useRequest(downloadRequest);

    const [products, setProducts] = useState<Product[]>([]);
    const [showProductBulkUpload, setShowProductBulkUpload] = useState(false);
    const bulkRequest = country === DeclarationCountry.UK ? createUkBulkProducts : createBulkProducts;
    const { doRequest: createBulk } = useRequest(bulkRequest);
    const [messages, setMessages] = useState<{ message: string; type: 'success' | 'error' }[]>([]);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);

    const showMessage = (message: string, type: 'success' | 'error') => {
        setMessages((prev) => [...prev, { message, type }]);
    };

    useEffect(() => {
        if (uploaded) {
            if (uploadedProducts && !uploadError) {
                setProducts(products);
                onClose();
                setShowProductBulkUpload(true);
            } else {
                Notification({
                    type: 'error',
                    messageTitle: t('bulk-upload.error-title'),
                    description: t(t('bulk-upload.error-message')),
                });
            }
            setUploaded(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadedProducts, uploaded, uploadError]);

    const uploadFile = async (options: any) => {
        const { file } = options;
        const response = await uploadProduct(file);
        if (response) {
            setUploaded(true);
        }

        return response;
    };

    const handleCreateBulk = async () => {
        const productsWithTags = products.map((p) => ({ ...p, tags: selectedTags }));
        const createBulkData = await createBulk(productsWithTags);

        if (createBulkData !== null) {
            onProductTemplatesCreated?.();
            setShowProductBulkUpload(false);
            showMessage(t('bulk-upload.success-message'), 'success');
        } else {
            showMessage(t('bulk-upload.error-message'), 'error');
        }
    };

    const templateFormValues = getTemplateFormValues(mode);

    const countrySelectOptions = useMemo(
        () =>
            [
                { id: 'IRELAND', value: '' },
                { id: 'UK', value: '' },
                ...(mode === 'declaration' ? [{ id: 'EU', value: '' }] : []),
            ] as { id: string; value: string }[],
        [mode]
    );

    // A template without a name shows its id instead, because the select hides the keys.
    const declarationTemplateSelectOptions = (templateOptions ?? []).map(({ id, value }) => ({
        id,
        value: value ?? '',
    }));

    const [templateNames, setTemplateNames] = useState<Set<string> | undefined>(undefined);
    const validationSchema = getCreateTemplateValidationSchema(templateNames);

    // Opens on the params applied on the dashboard. Reinitialising keeps it in step when they change.
    const initialValues = getCreateTemplateInitialValues({ country, internalType, formType: messageType, mode });

    const formik = useFormik<FormData>({
        initialValues,
        enableReinitialize: true,
        validateOnChange: false,
        validateOnBlur: true,
        validateOnMount: true,
        onSubmit: () => {},
        validationSchema,
    });

    useEffect(() => {
        if (!formik.values.formType) return;
        getAllDeclarationTemplateNames({ templateData: formik.values as any }).then(setTemplateNames);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.formType]);

    const handleCancel = () => {
        onClose();
        formik.resetForm();
    };

    const handleCreate = async () => {
        if (mode === 'product') {
            if (!(formik.values.formType && formik.values.declarationType && formik.values.country)) return;
            navigate(
                `/customs-declarations/${formik.values.country.toLowerCase()}/${
                    formik.values.declarationType === 'IMPORT_NEW' ? 'IMPORT' : formik.values.declarationType
                }/products/${formik.values.formType}/new${
                    formik.values.declarationTemplateId
                        ? `?declarationTemplateId=${formik.values.declarationTemplateId}`
                        : ''
                }`,
                // Pass the team id to the navigation state
                {
                    state: {
                        teamId: formik.values.teamId,
                    },
                }
            );
            return;
        }
        const valid = await formik.validateForm();
        if (!isEmpty(valid)) {
            formik.setTouched(setNestedObjectValues(valid, true));
            return;
        }
        handleCancel();
        openModal?.(formik.values as any);
        navigate('add');
    };

    const declarationTypeBasedOnCountry = useMemo(
        () => templateFormValues[getFormikProps('country', formik).fieldProps.value as keyof typeof templateFormValues],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [formik]
    );

    const formTypeBasedOnDeclarationType = useMemo(
        () =>
            declarationTypeBasedOnCountry?.[
                getFormikProps('declarationType', formik).fieldProps.value as keyof typeof declarationTypeBasedOnCountry
            ] as string[] | undefined,
        [formik, declarationTypeBasedOnCountry]
    );

    const getDeclarationTypeCodelist = useMemo(() => {
        if (!declarationTypeBasedOnCountry) return;

        return Object.keys(declarationTypeBasedOnCountry).map((key) => ({
            id: key,
            value: '',
        }));
    }, [declarationTypeBasedOnCountry]);

    const getFormTypeCodelist = useMemo(() => {
        if (!(declarationTypeBasedOnCountry || formTypeBasedOnDeclarationType)) return;

        return formTypeBasedOnDeclarationType?.map((code) => ({ id: code, value: '' }));
    }, [declarationTypeBasedOnCountry, formTypeBasedOnDeclarationType]);

    useEffect(() => {
        const declarationType = getFormikProps('declarationType', formik).fieldProps.value;

        if (!find(getDeclarationTypeCodelist, (code: any) => code.id === declarationType)) {
            const declarationTypeField = formik.getFieldHelpers('declarationType');
            const formTypeField = formik.getFieldHelpers('formType');

            declarationTypeField.setValue(null);
            declarationTypeField.setTouched(false);

            formTypeField.setValue(null);
            formTypeField.setTouched(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik?.values]);

    return (
        <Drawer
            open={visible}
            width={550}
            onClose={onClose}
            placement="right"
            headerStyle={{
                display: 'none',
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <div
                    style={{
                        display: 'inline-flex',
                        flexDirection: 'row',
                        gap: '16px',
                        justifyContent: 'start',
                    }}
                >
                    <StyledCloseOutlined onClick={() => onClose()} />
                    <H5>Create New Template</H5>
                </div>
                <div
                    style={{
                        display: formik.values.formType === 'H1 Bulk Upload' ? 'none' : 'flex',
                        gap: '1rem',
                        justifyContent: 'flex-end',
                    }}
                >
                    <Button
                        onClick={handleCancel}
                        style={{
                            borderRadius: '6px',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleCreate}
                        style={{
                            borderRadius: '6px',
                        }}
                    >
                        Create
                    </Button>
                </div>
            </div>
            <Container>
                <LabeledField label="Country" htmlFor="country" required>
                    <FormSelect
                        style={{ maxWidth: '100%' }}
                        hideKeys
                        selectOptions={countrySelectOptions}
                        {...getFormikProps('country', formik)}
                    />
                </LabeledField>
                <LabeledField label="Declaration Type" htmlFor="declarationType" required>
                    <FormSelect
                        style={{ maxWidth: '100%' }}
                        hideKeys
                        selectOptions={getDeclarationTypeCodelist}
                        {...getFormikProps('declarationType', formik)}
                    />
                </LabeledField>
                <LabeledField label="Form Type" htmlFor="formType" required>
                    <FormSelect
                        style={{ maxWidth: '100%' }}
                        hideKeys
                        selectOptions={getFormTypeCodelist}
                        {...getFormikProps('formType', formik)}
                    />
                </LabeledField>
                {mode === 'declaration' ? (
                    <LabeledField label="Template Name" htmlFor="name" required>
                        <FormInput
                            condensed
                            placeholder="Enter Template Name"
                            {...getFormikProps('name', formik)}
                            inputStyles={{
                                width: '100%',
                                maxWidth: '100%',
                            }}
                        />
                    </LabeledField>
                ) : (
                    formik.values.formType !== 'H1 Bulk Upload' && (
                        <LabeledField label="Declaration Template" htmlFor="declarationTemplateId">
                            <FormSelect
                                style={{ maxWidth: '100%' }}
                                placeholder="Default"
                                hideKeys
                                disabled={!templateOptions}
                                selectOptions={declarationTemplateSelectOptions}
                                {...getFormikProps('declarationTemplateId', formik)}
                            />
                        </LabeledField>
                    )
                )}

                {isFreightForwarder && (
                    <LabeledField label="Team" htmlFor="teamId">
                        <TeamSelectField
                            fieldHelper={formik.getFieldHelpers('teamId')}
                            fieldMeta={formik.getFieldMeta('teamId')}
                            fieldProps={formik.getFieldProps('teamId')}
                        />
                    </LabeledField>
                )}

                {mode === 'product' && formik.values.formType === 'H1 Bulk Upload' && (
                    <BulkUpload
                        // hasDontOverrideFirstRowText
                        fileType={[FileTypes.CSV, FileTypes.XLS, FileTypes.XLSX]}
                        onDownload={downloadTemplate}
                        onUpload={uploadFile}
                    />
                )}
            </Container>

            <CustomModal
                title={
                    <h4
                        style={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                        }}
                    >
                        Product Bulk Upload
                    </h4>
                }
                open={showProductBulkUpload}
                width={1143}
                testId="continue-modal"
                confirmText="Continue"
                cancelText="Cancel"
                onCancel={() => setShowProductBulkUpload(false)}
                onOk={() => handleCreateBulk()}
                style={{
                    borderRadius: '6px',
                }}
            >
                <ProductBulkUploadModal
                    products={products}
                    selectedTags={selectedTags}
                    onTagsSelected={setSelectedTags}
                    country={formik.values.country as DeclarationCountry}
                    internalType={formik.values.declarationType as DeclarationInternalType}
                    messageType={formik.values.formType as MessageTypes}
                />
            </CustomModal>
        </Drawer>
    );
};

export default CreateTemplateDrawer;
